import { HttpParams } from '@angular/common/http';
import { DEFAULT_FORMATION_LEAD_EMAILS } from '@app/shared/constants/formation-lead.constant';
import { UserService } from '@app/shared/services/user.service';
import { FormationLead, User } from 'lfx-pcc';
import { MessageService } from 'primeng/api';
import { catchError, forkJoin, map, Observable, of, shareReplay, take } from 'rxjs';

export type FormationLeadEmptyMode = 'omit' | 'clear';

// Cached once per session: the suggested-leads list is static, so avoid re-resolving on every dialog/form open.
let defaultFormationLeads$: Observable<User[]> | null = null;

export function resolveFormationLeadEmail(lead: FormationLead | User): string {
  if (lead.Email) {
    return lead.Email;
  }

  if ('Emails' in lead) {
    return lead.Emails?.[0]?.EmailAddress || '';
  }

  return '';
}

export function resolveFormationLeadAvatar(lead: FormationLead | User): string {
  if ('Avatar' in lead && lead.Avatar) {
    return lead.Avatar;
  }

  if ('LogoURL' in lead) {
    return lead.LogoURL || '';
  }

  return '';
}

export function buildFormationLeadFromSelection(lead: unknown, emptyMode: FormationLeadEmptyMode = 'omit'): FormationLead | undefined {
  if (!lead || typeof lead !== 'object' || !('Name' in lead) || typeof (lead as FormationLead).Name !== 'string') {
    return emptyMode === 'clear' ? ({} as FormationLead) : undefined;
  }

  const selection = lead as FormationLead | User;
  const email = resolveFormationLeadEmail(selection);
  if (!email) {
    return emptyMode === 'clear' ? ({} as FormationLead) : undefined;
  }

  return {
    Name: selection.Name,
    Email: email,
    Avatar: resolveFormationLeadAvatar(selection),
  };
}

export function searchFormationLeadUsers(userService: UserService, messageService: MessageService, query: string): Observable<User[]> {
  const params = new HttpParams({ fromObject: { name: query.trim(), pageSize: '10' } });

  return userService.staffSearchUsers(params).pipe(
    map((res) => res.Data || []),
    catchError(() => {
      messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to search users. Please try again or contact support.',
        key: 'support',
      });
      return of([]);
    }),
    take(1)
  );
}

export function loadDefaultFormationLeads(userService: UserService): Observable<User[]> {
  if (!defaultFormationLeads$) {
    // swallowErrors=false so a genuine no-match caches a partial list, while a real request failure invalidates and retries.
    defaultFormationLeads$ = forkJoin(DEFAULT_FORMATION_LEAD_EMAILS.map((email) => userService.searchUserByEmail(email, false))).pipe(
      map((users) => users.filter((user): user is User => user !== null)),
      catchError(() => {
        defaultFormationLeads$ = null;
        return of([]);
      }),
      shareReplay(1)
    );
  }

  return defaultFormationLeads$;
}
